import { canv } from "@/canv/global";
import { getTarget, sleep } from "@/canv/utils";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { eventEmitter } from "@/eventEmitter";
import type { Group } from "@/group";
import { showSnackbar } from "@/snackbar/snackbar";
import { BaseExport } from "./BaseExport";
import { collectFrameGroups } from "./utils";
import { VectorExport } from "./VectorExport";

type ExportState = {
    type: "base" | "vector" | null;
    structure: "page" | "pages" | null;
};

export class ExportWindow {
    constructor() {
        this.baseExport = new BaseExport();
        this.vectorExport = new VectorExport();
    }

    open() {
        const exportWindowWrapper = document.createElement("div");
        exportWindowWrapper.classList.add("exportWindowWrapper");
        document.body.prepend(exportWindowWrapper);
        this.initWrapper();

        const exportWindow = document.createElement("div");
        exportWindow.classList.add("exportWindow");
        exportWindowWrapper.prepend(exportWindow);

        const exportButton = document.getElementsByClassName("barPanelExport")[0];
        if (exportButton) {
            const buttonRect = exportButton.getBoundingClientRect();
            const windowRect = exportWindow.getBoundingClientRect();
            let leftPos = buttonRect.right - windowRect.width;
            // Минимальное значение left = 68px
            if (leftPos < 68) {
                leftPos = 68;
            }

            exportWindow.style.left = `${leftPos}px`;
        }

        const { groupsWithFrame, groupsWithoutFrame } = collectFrameGroups(
            canv.canvasController.getSelected(),
        );

        this.groupsWithFrame = groupsWithFrame;
        this.groupsWithoutFrame = groupsWithoutFrame;
        this.state.type = "base";
        this.state.structure = "page";

        this.draw();
        this.init();

        eventEmitter.dispatch("header.button.active.name", {
            name: "export",
        });
    }

    initWrapper() {
        const exportWindowWrapper = document.querySelector(".exportWindowWrapper");
        exportWindowWrapper?.addEventListener("mousedown", this.wrapperClick);
    }

    wrapperClick = (e) => {
        const tryInner = getTarget(e, "exportWindow");
        if (tryInner == null) {
            this.close();
        }
    };

    close() {
        const exportWindow = document.querySelector(".exportWindow");
        if (exportWindow) {
            exportWindow.removeEventListener("change", this.changeExportOptions);
        }

        const exportWindowDownloadButton = document.querySelector(".exportWindowDownloadButton");
        if (exportWindowDownloadButton) {
            exportWindowDownloadButton.removeEventListener("mouseup", this.onClickExport);
        }

        const exportWindowWrapper = document.querySelector(".exportWindowWrapper");
        if (exportWindowWrapper) {
            exportWindowWrapper.outerHTML = "";
        }

        eventEmitter.dispatch("header.button.active.name", {
            name: null,
        });
    }

    init() {
        const exportWindow = document.querySelector(".exportWindow");
        if (exportWindow) {
            exportWindow.addEventListener("change", this.changeExportOptions);
        }

        const exportWindowDownloadButton = document.querySelector(".exportWindowDownloadButton");
        if (exportWindowDownloadButton) {
            exportWindowDownloadButton.addEventListener("mouseup", this.onClickExport);
        }
    }

    changeExportOptions = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (!target) return;

        if (target.name === "type") {
            this.state.type = target.value as ExportState["type"];
            this.updateFileType();
            this.updateFileStructure();
        } else if (target.name === "structure") {
            this.state.structure = target.value as ExportState["structure"];
            this.updateFileStructure();
        }
    };

    updateFileType() {
        const exportWindow = document.querySelector(".exportWindow");
        const fileTypeElement = exportWindow?.querySelector(".exportWindowFileType");
        if (!fileTypeElement) return;

        fileTypeElement.innerHTML = this.renderFileType();
    }

    updateFileStructure() {
        const exportWindow = document.querySelector(".exportWindow");
        const fileStructureElement = exportWindow?.querySelector(".exportWindowFileStructure");
        if (!fileStructureElement) return;

        fileStructureElement.innerHTML = this.renderFileStructure();
    }

    draw() {
        const exportWindow = document.querySelector(".exportWindow");
        if (!exportWindow) return;

        const downloadText = this.groupsWithFrame.length
            ? `Скачать выделенное (${this.groupsWithFrame.length})`
            : "Скачать";

        exportWindow.innerHTML = `
            <div class="exportWindowTitle">Параметры экспорта</div>
            <hr class="exportWindowDivider" />

            <div class="exportWindowFileType">
                ${this.renderFileType()}
            </div>

            <div class="exportWindowFileStructure">
                ${this.renderFileStructure()}
            </div>

            <div class="exportWindowBottom">
                <button class="exportWindowDownloadButton">${downloadText}</button>
            </div>
    `;
    }

    renderFileType() {
        return `
            <div class="exportWindowSubtitle">Тип файла</div>
            ${this.renderRadio("type", "base", "PDF")}
            ${this.renderRadio("type", "vector", "PDF в высоком разрешении")}
    `;
    }

    renderFileStructure() {
        if (
            this.groupsWithFrame.length < 2 ||
            !(this.state.type === "base" || this.state.type === "vector")
        )
            return "";

        return `
            <div class="exportWindowSubtitle">Структура файла</div>
            ${this.renderRadio("structure", "page", "Всё на одной странице")}
            ${this.renderRadio("structure", "pages", "По странице на фрейм", "Нумерация по расположению фреймов на доске: слева направо и сверху вниз")}
    `;
    }

    renderRadio(name: string, value: string, label: string, description?: string) {
        const checked = this.state[name] === value ? "checked" : "";

        return `
            <div class="exportWindowOption">
                <label>
                    <input type="radio" name="${name}" value="${value}" ${checked}>
                    <span>${label}</span>
                </label>
                ${this.state[name] === value && description ? `<p class="exportWindowOptionDescription">${description}</p>` : ""}
            </div>
        `;
    }

    onClickExport = async () => {
        if (!(this.state.type && this.state.structure)) return;

        this.close();

        eventEmitter.dispatch("button.disabled", {
            export: true,
        });
        canv.multiManager.changeVisibilityCursor(false);

        const loadingSnackbar = showSnackbar("Идёт экспорт", {
            type: "loading",
        });

        // Даем браузеру время на обновление UI перед началом вычислений
        await sleep(0);

        try {
            await this.exportByState();
            showSnackbar("Экспорт завершён", {
                duration: SNACKBAR_TIME,
                type: "success",
            });
        } catch {
            showSnackbar("Ошибка экспорта. Попробуйте ещё раз");
        } finally {
            loadingSnackbar.close();
            eventEmitter.dispatch("button.disabled", {
                export: false,
            });

            if (!canv.multiManager.disableMultiCursors) {
                canv.multiManager.changeVisibilityCursor(true);
            }
        }
    };

    async exportByState() {
        const exportQuality = this.state.type === "vector" ? this.vectorExport : this.baseExport;

        if (!this.groupsWithFrame.length) {
            return await exportQuality.exportViewport();
        }

        if (this.state.structure === "page") {
            return await exportQuality.exportPage(this.groupsWithFrame, this.groupsWithoutFrame);
        }

        if (this.state.structure === "pages") {
            return await exportQuality.exportPages(this.groupsWithFrame, this.groupsWithoutFrame);
        }
    }

    private groupsWithFrame: Group[] = [];
    private groupsWithoutFrame: Group[] = [];
    private state: ExportState = { type: null, structure: null };
    private readonly baseExport: BaseExport;
    private readonly vectorExport: VectorExport;
}
